import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../AuthContext';
import { useSubAgentAuth } from '../SubAgent/SubAgentAuthContext';
import StudentPreferences from '../../../components/StudentPreferences';
import InitiateServices from '../../../components/InitiateServices';
import RequestDocument from '../../../components/RequestDocument';
import Tasks from '../../../components/Tasks';
import PersonalDetails from '../../../components/PersonalDetails';
import AcademicResults from '../../../components/AcademicResults';
import TestResults from '../../../components/TestResults';
import UploadedDocuments from '../../../components/UploadedDocuments';
import CallLogs from '../../../components/CallLogs';
import FollowUps from '../../../components/FollowUps';
import AdmissionDetails from '../../../components/AdmissionDetails';
import MyTasks from '../../../components/MyTasks';
import AppliedUniversities from '../../../components/AppliedUniversities';
import Registrations from '../../../components/Registrations';
import UniversityPayment from '../../../components/UniversityPayment';


const StudentDetails = () => {

    const { departmentData } = useAuth();

    const { subAgentPermissions } = useSubAgentAuth();
    const [activeSection, setActiveSection] = useState<string>('');

    useEffect(() => {

        if (departmentData) {
            // get the first section from the department data which read is true
            const firstSection = Object.values(departmentData?.tabPermissions)
                .find(permission => permission.read === true)?.name || '';
            setActiveSection(firstSection);
        } else if (subAgentPermissions) {
            // get the first section from the sub agent permissions which read is true
            const firstSection = subAgentPermissions?.tabPermissions
                .find((permission: { read: boolean; }) => permission.read === true)?.name || '';
            setActiveSection(firstSection);
        }
    }, [departmentData, subAgentPermissions]);

    // Sections array
    let sections: string[] = [];
    if (departmentData) {
        sections = Object.values(Object.values(departmentData?.tabPermissions))
            .filter(permission => permission.read === true)
            .map(permission => permission.name) || [];
    } else if (subAgentPermissions) {
        sections = subAgentPermissions?.tabPermissions
            .filter((permission: { read: boolean; }) => permission.read === true)
            .map((permission: { name: string; }) => permission.name) || [];
    }

    const getWritePermission = (section: string) => {
        if (departmentData) {
            return Object.values(departmentData?.tabPermissions)
                .find(permission => permission.name === section)?.write || false;
        } else if (subAgentPermissions) {
            return subAgentPermissions?.tabPermissions
                .find((permission: { name: string; }) => permission.name === section)?.write || false;
        }
        return false;
    }

    const progressSteps = [
        { label: "Student Preferences", status: "completed" },
        { label: "Personal Details", status: "completed" },
        { label: "Academic Results", status: "active" },
        { label: "Test Results", status: "pending" },
        // { label: "Uploaded Documents", status: "pending" }
    ];
    const admissionProgressSteps = [
        { label: "Applied Universities", status: "completed" },
        { label: "Admission Details", status: "active" },
        // { label: "Upload Documents", status: "pending" },
    ];
    const taskProgressSteps = [
        { label: "My Tasks", status: "pending" },
        { label: "Student Tasks", status: "active" },
    ];
    const [activeProfileStep, setActiveProfileStep] = useState("Student Preferences");
    const [activeAdmissionStep, setActiveAdmissionStep] = useState("Applied Universities");
    const [activeTaskStep, setActiveTaskStep] = useState("My Tasks");

    // Common handlers
    const handleSectionClick = (section: string) => {
        setActiveSection(section);
    };

    return (
        <div>
            <div className="flex ml-5 space-x-4 overflow-x-auto mb-6 custom-scrollbar">
                {sections.map((section, index) => {
                    const isDisabled = section === "" ; // Add any other conditions here if needed

                    return (
                        <button
                            key={index}
                            className={`py-2 px-5 rounded-3xl text-center 
                    ${activeSection === section
                                    ? "bg-sky-950 text-white"
                                    : "bg-gray-100 text-gray-800"
                                }
                    ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                            onClick={(e) => {
                                if (!isDisabled) {
                                    handleSectionClick(section);
                                    e.stopPropagation();
                                }
                            }}
                            disabled={isDisabled}
                        >
                            {section}
                        </button>
                    );
                })}
            </div>

            {activeSection === "Initiate Services" && (
                <InitiateServices write={getWritePermission('Initiate Services')} />
            )}

            {activeSection === "Admission" && (
                <div className="complete-profile-section ml-12">
                    {/* Progress Steps Bar */}
                    <div className="relative flex w-full">
                        <div className="relative w-full max-w-3xl">
                            <div className="absolute top-2 left-[5%] right-[5%] border-t-2 border-dotted border-[#3483BE]" />
                            <div className="relative flex justify-between w-full">
                                {admissionProgressSteps.map((step, index) => (
                                    <div
                                        key={index}
                                        className="flex flex-col items-center  cursor-pointer"
                                        onClick={() => setActiveAdmissionStep(step.label)}
                                    >
                                        <div
                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center 
                            ${step.label === activeAdmissionStep
                                                    ? "border-[#3483BE] bg-white"
                                                    : index < admissionProgressSteps.findIndex(s => s.label === activeAdmissionStep)
                                                        ? "border-[#3483BE] bg-[#3483BE]"
                                                        : "border-[#3483BE] bg-white"
                                                }`}
                                        >
                                            {step.label === activeAdmissionStep && (
                                                <div className="w-3 h-3 rounded-full bg-gradient-to-t from-[#3483BE] to-[#02385E]"></div>
                                            )}
                                        </div>
                                        <span className="text-xs mt-2 text-center w-24 text-gray-600">{step.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    {/* Content for each step */}
                    <div className="mt-8">
                        {activeAdmissionStep === "Applied Universities" && (
                            <AppliedUniversities write={getWritePermission("Admission")} />
                        )}

                        {activeAdmissionStep === "Admission Details" && (
                            <AdmissionDetails write={getWritePermission("Admission")} />
                        )}
                        {/* {activeAdmissionStep === "Upload Documents" && (
                            <div className="request-doc-section">
                                <RequestDocument write={getWritePermission("Admission")} />
                            </div>
                        )} */}
                    </div>
                </div>
            )}

            {activeSection === "Documents" && (
                <div className="request-doc-section">
                    <RequestDocument write={getWritePermission("Documents")} />
                </div>
            )}

            {activeSection === "Complete Profile" && (
                <div className="complete-profile-section ml-12">
                    {/* Progress Steps Bar */}
                    <div className="relative flex w-full">
                        <div className="relative w-full max-w-3xl">
                            <div className="absolute top-2 left-[5%] right-[5%] border-t-2 border-dotted border-[#3483BE]" />
                            <div className="relative flex justify-between w-full">
                                {progressSteps.map((step, index) => (
                                    <div
                                        key={index}
                                        className="flex flex-col items-center  cursor-pointer"
                                        onClick={() => setActiveProfileStep(step.label)}
                                    >
                                        <div
                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center 
                                ${step.label === activeProfileStep
                                                    ? "border-[#3483BE] bg-white"
                                                    : index < progressSteps.findIndex(s => s.label === activeProfileStep)
                                                        ? "border-[#3483BE] bg-[#3483BE]"
                                                        : "border-[#3483BE] bg-white"
                                                }`}
                                        >
                                            {step.label === activeProfileStep && (
                                                <div className="w-3 h-3 rounded-full bg-gradient-to-t from-[#3483BE] to-[#02385E]"></div>
                                            )}
                                        </div>
                                        <span className="text-xs mt-2 text-center w-24 text-gray-600">{step.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Content for each step */}
                    <div className="mt-8">
                        {activeProfileStep === "Student Preferences" && (
                            <StudentPreferences write={getWritePermission("Complete Profile")} />
                        )}
                        {activeProfileStep === "Personal Details" && (
                            <PersonalDetails write={getWritePermission("Complete Profile")} />
                        )}

                        {activeProfileStep === "Academic Results" && (
                            <AcademicResults write={getWritePermission("Complete Profile")} />

                        )}

                        {activeProfileStep === "Test Results" && (
                            <TestResults write={getWritePermission("Complete Profile")} />

                        )}

                        {activeProfileStep === "Uploaded Documents" && (
                            <UploadedDocuments write={getWritePermission("Complete Profile")} />

                        )}


                    </div>
                </div>
            )}


            {activeSection === "Tasks" && (
                <div className="complete-profile-section ml-12">
                    {/* Progress Steps Bar */}
                    <div className="relative flex w-full">
                        <div className="relative w-full max-w-3xl">
                            <div className="absolute top-2 left-[5%] right-[5%] border-t-2 border-dotted border-[#3483BE]" />
                            <div className="relative flex justify-between w-full">
                                {taskProgressSteps.map((step, index) => (
                                    <div
                                        key={index}
                                        className="flex flex-col items-center  cursor-pointer"
                                        onClick={() => setActiveTaskStep(step.label)}
                                    >
                                        <div
                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center 
                        ${step.label === activeTaskStep
                                                    ? "border-[#3483BE] bg-white"
                                                    : index < taskProgressSteps.findIndex(s => s.label === activeTaskStep)
                                                        ? "border-[#3483BE] bg-[#3483BE]"
                                                        : "border-[#3483BE] bg-white"
                                                }`}
                                        >
                                            {step.label === activeTaskStep && (
                                                <div className="w-3 h-3 rounded-full bg-gradient-to-t from-[#3483BE] to-[#02385E]"></div>
                                            )}
                                        </div>
                                        <span className="text-xs mt-2 text-center w-24 text-gray-600">{step.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    {/* Content for each step */}
                    <div className="mt-8">
                        {activeTaskStep === "Student Tasks" && (
                            <Tasks write={getWritePermission("Tasks")} />
                        )}
                        {activeTaskStep === "My Tasks" && (
                            <MyTasks write={getWritePermission("Tasks")} />
                        )}
                    </div>
                </div>
            )}


            {activeSection === "History" && (
                <div className="flex w-full gap-x-4 ">
                    <div className="w-1/2 border-gray-200">
                        <CallLogs />
                    </div>
                    <div className="w-1/2">
                        <FollowUps />
                    </div>
                </div>
            )}

            {activeSection === "Registration" && (
                <Registrations write={getWritePermission('Registration')} />
            )}

            {activeSection === "University Payout" && (
                <UniversityPayment write={getWritePermission('University Payout')} />
            )}

        </div>
    );
};

export default StudentDetails;  